import { lib } from '../scene/video.js';
import { UNWRAP_LIMIT } from '../services/limits.js';

/* READING THE CLIP. Unwrap's own decoder, separate from scene/video.js on purpose.
   That one belongs to a clip ON a scene object: it is keyed by object id, feeds a
   three.js texture, is capped at 1920 wide and chases a playhead. This one just
   reads a file — a picture of any instant to mark corners on, and every frame in
   order for the export — through the same mediabunny the rest of the app uses.

   Nothing is stored or uploaded. The File is read in place (BlobSource slices it as
   needed, so a 400 MB phone recording is never held in memory), which is also why
   there is no IndexedDB and no account involved just to look at a clip.

   Rotation is the file's own: phones record sideways and say so in metadata, and
   mediabunny applies it by default, so the sizes here are what the viewer sees. */

const IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.avif'];
/** A picture rather than a video, by its type or, failing that, its name. */
export const isImageFile = f => String(f?.type || '').startsWith('image/') || IMAGE_EXT.some(e => String(f?.name || '').toLowerCase().endsWith(e));

/**
 * A single picture, offered in the shape of a clip so the page can treat it as one with no length: the same
 * corners, the same flat preview and the same still export, just no video to play or export. Handy for trying
 * the corners on a photograph. Larger than the encoder's ceiling it is brought down to it, which a still
 * does not mind. Throws with a sentence for the person.
 */
export async function openImage(file) {
  if (file.size > UNWRAP_LIMIT.bytes) throw new Error(`That file is ${(file.size / 1048576).toFixed(0)} MB. The limit is ${(UNWRAP_LIMIT.bytes / 1048576).toFixed(0)} MB.`);
  let bmp;
  try { bmp = await createImageBitmap(file); }
  catch (_) { throw new Error('That picture could not be opened. PNG, JPEG, WebP and similar work; HEIC (iPhone) may need converting.'); }
  const k = Math.min(1, UNWRAP_LIMIT.longSide / Math.max(bmp.width, bmp.height));
  const w = Math.max(2, Math.round(bmp.width * k)), h = Math.max(2, Math.round(bmp.height * k));
  const base = document.createElement('canvas');
  base.width = w; base.height = h;
  base.getContext('2d').drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  const scaled = maxW => {
    const pw = Math.min(w, Math.max(64, Math.round(maxW)));
    if (pw === w) return base;
    const c = document.createElement('canvas');
    c.width = pw; c.height = Math.max(2, Math.round(pw * h / w));
    c.getContext('2d').drawImage(base, 0, 0, c.width, c.height);
    return c;
  };
  const copy = c => { const o = document.createElement('canvas'); o.width = c.width; o.height = c.height; o.getContext('2d').drawImage(c, 0, 0); return o; };
  return {
    name: file.name, size: file.size, w, h, fps: 1, duration: 0, codec: 'image', still: true,
    async frameAt(_t, maxW = 1280) { return { canvas: copy(scaled(maxW)), timestamp: 0 }; },
    playFrom() { return (async function* () {})(); },
    framesAt() { return (async function* () { yield { canvas: copy(base), timestamp: 0, duration: 1 }; })(); },
    frames() { return (async function* () { yield { canvas: copy(base), timestamp: 0, duration: 1 }; })(); },
    close() { /* nothing held open */ }
  };
}

/**
 * Open a clip, check it can be used, and say what it is.
 *
 * Throws with a sentence meant for the person: too big, too long, not a video, or
 * a codec this browser cannot decode (an iPhone's HEVC is the usual one).
 */
export async function openClip(file) {
  if (!file) throw new Error('Choose a video file.');
  if (file.size > UNWRAP_LIMIT.bytes)
    throw new Error(`That file is ${(file.size / 1048576).toFixed(0)} MB. The limit is ${(UNWRAP_LIMIT.bytes / 1048576).toFixed(0)} MB.`);

  const { Input, BlobSource, MP4, QTFF, MATROSKA, WEBM, CanvasSink, canDecodeVideo } = await lib();
  const input = new Input({ formats: [MP4, QTFF, MATROSKA, WEBM], source: new BlobSource(file) });
  let track;
  try { track = await input.getPrimaryVideoTrack(); }
  catch (_) { throw new Error('That does not look like a video file. MP4, MOV and WebM work.'); }
  if (!track) throw new Error('That file has no video in it.');

  const codec = await track.getCodec();
  const w = await track.getDisplayWidth(), h = await track.getDisplayHeight();
  const duration = await input.computeDuration();
  if (!(duration > 0)) throw new Error('That video has no length.');
  if (duration > UNWRAP_LIMIT.seconds)
    throw new Error(`That clip is ${Math.round(duration)} seconds. The limit is ${UNWRAP_LIMIT.seconds} (${UNWRAP_LIMIT.seconds / 60} minutes).`);
  if (Math.max(w, h) > UNWRAP_LIMIT.longSide)
    throw new Error(`That video is ${w} × ${h}. The longest side can be at most ${UNWRAP_LIMIT.longSide} pixels.`);
  /* Asked now, not discovered on the first frame: a container this browser can read
     is not a codec it can decode, and an H.265 MP4 opens fine and then shows nothing. */
  if (!(await canDecodeVideo(codec, { width: w, height: h })))
    throw new Error(`This browser cannot decode ${codec} video at ${w} × ${h}. Convert it to H.264 (MP4), or try Chrome or Edge.`);

  /* Sampled, not assumed: it is what the unwrapped file is written at. A variable-rate
     clip has no single answer, and each frame keeps its own timestamp in the export
     anyway, so this is only the rate the file announces. */
  let fps = 30;
  try {
    const stats = await track.computePacketStats?.(200);
    if (stats?.averagePacketRate > 0) fps = Math.round(stats.averagePacketRate * 1000) / 1000;
  } catch (_) { /* best effort */ }

  /* Pictures to mark corners on: one sink per size, small enough to be quick. */
  const previews = new Map();
  const previewSink = maxW => {
    const pw = Math.min(w, Math.max(64, Math.round(maxW)));
    let s = previews.get(pw);
    if (!s) {
      s = new CanvasSink(track, { width: pw, height: Math.max(2, Math.round(pw * h / w)), fit: 'fill', poolSize: 2 });
      previews.set(pw, s);
    }
    return s;
  };

  return {
    name: file.name, size: file.size, w, h, fps, duration, codec,

    /**
     * A picture of the clip at time `t`, at most `maxW` wide, in a canvas of its own
     * (the sink recycles its canvases, so the pixels are copied out). Null past the end.
     */
    async frameAt(t, maxW = 1280) {
      const sink = previewSink(maxW);
      const at = Math.min(Math.max(0, t), Math.max(0, duration - 0.001));
      const wrapped = await sink.getCanvas(at);
      if (!wrapped) return null;
      const out = document.createElement('canvas');
      out.width = wrapped.canvas.width; out.height = wrapped.canvas.height;
      out.getContext('2d').drawImage(wrapped.canvas, 0, 0);
      return { canvas: out, timestamp: wrapped.timestamp };
    },

    /**
     * Frames from `t` onwards at preview size, in order — what playing the clip in the page reads.
     * Sequential, so each packet is decoded once; the caller paces them against the clock and drops
     * what it is too late for. Yields { canvas, timestamp, duration }, the canvas recycled.
     */
    playFrom(t, maxW = 1280) {
      return previewSink(maxW).canvases(Math.max(0, t));
    },

    /**
     * The frames at these instants, in the order given, at most `maxW` wide — a background for the
     * on-site export. The whole list must be handed over up front (the batch decoder cannot be fed
     * one instant at a time: see scene/video.js). Yields { canvas, timestamp, duration } or null past
     * the end; the canvas is recycled.
     */
    framesAt(times, maxW = 1920) {
      const pw = Math.min(w, Math.max(64, Math.round(maxW)));
      const sink = new CanvasSink(track, { width: pw, height: Math.max(2, Math.round(pw * h / w)), fit: 'fill', poolSize: 2 });
      return sink.canvasesAtTimestamps(times);
    },

    /**
     * Every frame, in order, at the clip's own size — what the export reads. Yields
     * { canvas, timestamp, duration }; the canvas is recycled, so use it before asking
     * for the next. Sequential, which is the fast way to decode: each packet once.
     */
    frames() {
      const sink = new CanvasSink(track, { width: w, height: h, fit: 'fill', poolSize: 2 });
      return sink.canvases();
    },

    close() { try { input.dispose?.(); } catch (_) { /* nothing to free */ } }
  };
}
